const dedicatedReports = [
  { routeName: 'app.conversions', groupBy: 'conversions' },
  { routeName: 'app.postbacks', groupBy: 'postbacks' },
  { routeName: 'app.errors', groupBy: 'errors' },
  { routeName: 'app.live-clicks', groupBy: 'live-clicks' },
  { routeName: 'app.live-visits', groupBy: 'live-visits' },
] as const;

const dedicatedReportRoutes = new Set<string>(
  dedicatedReports.map((item) => item.routeName),
);

const dedicatedReportGroupByKeys = new Set<string>(
  dedicatedReports.map((item) => item.groupBy),
);

const dedicatedReportGroupBy: Record<string, string> = Object.fromEntries(
  dedicatedReports.map((item) => [item.routeName, item.groupBy]),
);

const dedicatedReportRouteByGroup: Record<string, string> = Object.fromEntries(
  dedicatedReports.map((item) => [item.groupBy, item.routeName]),
);

const routesWithoutRowSelect = new Set([
  'app.conversions',
  'app.errors',
  'app.live-clicks',
  'app.live-visits',
]);

export const reportTableRouteNames = [
  'app.report',
  'app.sixth_sense',
  ...dedicatedReports.map((item) => item.routeName),
] as const;

export const isDedicatedReportRoute = (routeName: string | symbol | null | undefined) => {
  return typeof routeName === 'string' && dedicatedReportRoutes.has(routeName);
};

export const isDedicatedReportGroupBy = (groupBy: string) => {
  return dedicatedReportGroupByKeys.has(groupBy);
};

export const getDedicatedReportGroupBy = (routeName: string) => {
  return dedicatedReportGroupBy[routeName];
};

export const getDedicatedReportRouteName = (groupBy: string) => {
  return dedicatedReportRouteByGroup[groupBy] || 'app.report';
};

export const resolveReportTypeFromRoute = (
  routeName: string | symbol | null | undefined,
  routeType?: string | string[] | null,
) => {
  if (isDedicatedReportRoute(routeName)) {
    return getDedicatedReportGroupBy(routeName as string);
  }

  return routeType ?? undefined;
};

export const getReportTypeFromRouteName = (
  routeName: string | symbol | null | undefined,
  routeType?: string | string[] | null,
) => {
  if (routeName === 'app.report') {
    return routeType ?? null;
  }

  if (isDedicatedReportRoute(routeName)) {
    return getDedicatedReportGroupBy(routeName as string);
  }

  return null;
};

export const getReportRequestPath = (routeName: string | symbol | null | undefined) => {
  if (isDedicatedReportRoute(routeName)) {
    return `/report/${getDedicatedReportGroupBy(routeName as string)}`;
  }

  return '/report';
};

export const buildDedicatedReportTabPath = (
  routeName: string,
  data: string,
  report: string,
) => {
  return `/app/${getDedicatedReportGroupBy(routeName)}/${data}/${report}`;
};

export const supportsCompareReport = (routeName: string | symbol | null | undefined) => {
  return !isDedicatedReportRoute(routeName);
};

export const isReportTableRoute = (routeName: string | symbol | null | undefined) => {
  return typeof routeName === 'string' && (reportTableRouteNames as readonly string[]).includes(routeName);
};

export const shouldGenerateReportIds = (routeName: string | symbol | null | undefined) => {
  return isDedicatedReportRoute(routeName);
};

export const isDedicatedLiveReportRoute = (routeName: string | symbol | null | undefined) => {
  return routeName === 'app.live-clicks' || routeName === 'app.live-visits';
};

export const isDedicatedReportWithTextSearch = (routeName: string | symbol | null | undefined) => {
  return isDedicatedReportRoute(routeName) && !isDedicatedLiveReportRoute(routeName);
};

export const showsReportTableTotal = (routeName: string | symbol | null | undefined) => {
  return !isDedicatedReportRoute(routeName);
};

export const isPageWithoutGrouping = (routeName: string | symbol | null | undefined) => {
  return isDedicatedReportRoute(routeName);
};

export const resolveColumnStoreType = (routeName: string | symbol | null | undefined) => {
  if (isDedicatedReportRoute(routeName)) {
    return getDedicatedReportGroupBy(routeName as string);
  }

  return routeName;
};

export type ChartHashRouteContext = {
  routeName: string | symbol | null | undefined;
  from: unknown;
  to: unknown;
  timezone: unknown;
  compareEnabled: boolean;
  compareFrom: unknown;
  compareTo: unknown;
  compareDate: unknown;
  searchParams: Record<string, unknown>;
  treeLevels: unknown;
  view: unknown;
};

export const buildChartHashVarsForRoute = (context: ChartHashRouteContext) => {
  if (isDedicatedLiveReportRoute(context.routeName)) {
    return {};
  }

  if (isDedicatedReportWithTextSearch(context.routeName)) {
    return {
      from: context.from,
      to: context.to,
      searchParams: context.searchParams,
    };
  }

  return {
    from: context.from,
    to: context.to,
    timezone: context.timezone,
    ...(context.compareEnabled
      ? {
          compareEnabled: context.compareEnabled,
          compareFrom: context.compareFrom,
          compareTo: context.compareTo,
          compareDate: context.compareDate,
        }
      : {}),
    searchParams: context.searchParams,
    treeLevels: context.treeLevels,
    view: context.view,
  };
};

export const isReportRowSelectable = (routeName: string | symbol | null | undefined) => {
  return typeof routeName === 'string' && !routesWithoutRowSelect.has(routeName);
};
